import { useState, useMemo, useEffect, useRef } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, addDays, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, subMonths, subWeeks } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { ptBR } from 'date-fns/locale';
import { parseDate } from '@/lib/localizedDateUtils';
import { Calendar as CalendarIcon, List, Users } from 'lucide-react';
import { getLocalDayStart } from '@/lib/timezoneUtils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  MoreHorizontal, 
  RefreshCw, 
  Check, 
  X, 
  ExternalLink,
  CheckSquare,
  Square,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  Phone,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group';

import { 
  MoneyDisplay, 
  BusinessUnitSelector, 
  BrandSelector,
  AgingBadge,
  getAgingFaixa,
  FinancePageLayout,
} from '@/components/finance';
import { GroupedByClientTable, type ClientGroup } from '@/components/finance/GroupedByClientTable';
import { ColumnManager } from '@/components/reports/ColumnManager';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import type { Brand } from '@/components/finance/BrandSelector';
import {
  useReceivables,
  useMarkPaymentAsPaid,
  useCancelPayment,
  useBulkUpdatePayments,
  type ReceivablePayment,
} from '@/hooks/finance/useReceivables';
import { useBankAccounts } from '@/hooks/finance/useBankAccounts';
import { usePaymentAgreements } from '@/hooks/finance/usePaymentAgreements';
import { RequestRenegotiationModal } from '@/components/finance/collection/RequestRenegotiationModal';
import { RenegotiationApprovalQueue } from '@/components/finance/collection/RenegotiationApprovalQueue';
import { RegisterContactModal } from '@/components/finance/collection/RegisterContactModal';
import type { ContactReason, ContactType } from '@/hooks/finance/useCollectionHistory';
import { useTwilio } from '@/contexts/TwilioContext';

import { useReceivablesGroupedColumns } from '@/hooks/finance/useReceivablesGroupedColumns';
import { getEffectivePaymentStatus } from '@/lib/paymentStatus';
import { getStripeErrorMessage } from '@/lib/stripeErrorMessages';
import { getFullPhoneE164 } from '@/lib/utils';

type StatusFilter = 'all' | 'active' | 'pending' | 'overdue' | 'paid';
type DateFilterType = 'today' | 'yesterday' | 'this_week' | 'this_week_plus_last_week' | 'last_week' | 'this_month' | 'last_month' | 'next_30' | 'overdue_all' | 'custom';
type ViewMode = 'flat' | 'grouped';
type ChargeMethod = 'stripe' | 'zelle' | 'pix' | 'bank_transfer' | 'other';
type ReceivableOpsMeta = {
  contactId: string | null;
  hasCard: boolean;
  autoChargeEnabled: boolean;
  autoChargeStatus: string | null;
};

type SelectedCardInfo = {
  brand: string | null;
  last4: string | null;
  hasSavedCard: boolean;
};

type PaymentTxStats = {
  totalAttempts: number;
  successCount: number;
  failedCount: number;
  lastAt: string | null;
  lastStatus: string | null;
  lastMethod: string | null;
  lastPi: string | null;
};

type PaymentHistoryRow = {
  id: string;
  created_at: string | null;
  transaction_date: string | null;
  transaction_type: string | null;
  payment_method: string | null;
  status: string | null;
  amount: number | null;
  currency: string | null;
  error_code: string | null;
  reference_number: string | null;
  stripe_payment_intent_id: string | null;
  notes: string | null;
  confirmed_at: string | null;
};

type HistoryStatusFilter = 'all' | 'success' | 'failed' | 'pending';
type HistoryWindowFilter = 'all' | '7d' | '30d' | '90d';
type RegisterContactPrefill = {
  reason?: ContactReason;
  contact_type?: ContactType;
};

type PendingReceivableCall = {
  payment: ReceivablePayment;
  phoneE164: string;
  wasConnected: boolean;
};

const RECEIVABLES_MANUAL_METHOD_OPTIONS = [
  { value: 'pix', label: 'PIX' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'ach', label: 'ACH' },
  { value: 'wire', label: 'Wire' },
  { value: 'check', label: 'Cheque' },
  { value: 'cash', label: 'Dinheiro' },
] as const;

function formatMoney(value: number, currency: string = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

function normalizeHistoryStatus(status: string | null | undefined): 'success' | 'failed' | 'pending' {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'confirmed') return 'success';
  if (normalized === 'failed' || normalized === 'rejected') return 'failed';
  return 'pending';
}

function getHistoryStatusLabel(status: string | null | undefined) {
  const normalized = normalizeHistoryStatus(status);
  if (normalized === 'success') return 'Sucesso';
  if (normalized === 'failed') return 'Falha';
  return 'Pendente';
}

function chunkIds(ids: string[], size = 500): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

function getChargeErrorMessage(error: any): string {
  const status = error?.context?.status;
  const payload = error?.context?.json || error?.context?.body || null;
  const code =
    payload?.code ||
    payload?.error_code ||
    payload?.stripe_error_code ||
    payload?.stripeCode ||
    error?.code ||
    null;
  const message =
    payload?.message ||
    payload?.error ||
    error?.message ||
    'Falha ao processar cobrança';

  if (status === 402) {
    return getStripeErrorMessage(code || message, message);
  }
  if (status === 400 || status === 422) {
    return getStripeErrorMessage(code || message, message);
  }
  if (status === 401 || status === 403) {
    return 'Sem permissão para cobrar este pagamento';
  }
  if (status === 404) {
    return 'Pagamento ou cliente não encontrado para cobrança';
  }
  if (status === 429) {
    return 'Muitas tentativas de cobrança. Aguarde e tente novamente';
  }
  if (status && status >= 500) {
    return 'Serviço de cobrança indisponível. Tente novamente em instantes';
  }

  return getStripeErrorMessage(code || message, message);
}

async function fetchPaymentOpsMeta(paymentId: string): Promise<ReceivableOpsMeta | null> {
  const { data: paymentRow, error: paymentError } = await (supabase.from('payments') as any)
    .select('id,deal_id,auto_charge_enabled,auto_charge_status')
    .eq('id', paymentId)
    .maybeSingle();
  if (paymentError) throw paymentError;
  if (!paymentRow?.deal_id) return null;

  const { data: dealRow, error: dealError } = await (supabase.from('deals') as any)
    .select('id,contact_id')
    .eq('id', paymentRow.deal_id)
    .maybeSingle();
  if (dealError) throw dealError;
  if (!dealRow?.contact_id) {
    return {
      contactId: null,
      hasCard: false,
      autoChargeEnabled: Boolean(paymentRow.auto_charge_enabled),
      autoChargeStatus: paymentRow.auto_charge_status || null,
    };
  }

  const { data: contactRow, error: contactError } = await (supabase.from('contacts') as any)
    .select('id,has_payment_method,stripe_default_payment_method_id')
    .eq('id', dealRow.contact_id)
    .maybeSingle();
  if (contactError) throw contactError;

  return {
    contactId: dealRow.contact_id,
    hasCard: Boolean(contactRow?.has_payment_method) || Boolean(contactRow?.stripe_default_payment_method_id),
    autoChargeEnabled: Boolean(paymentRow.auto_charge_enabled),
    autoChargeStatus: paymentRow.auto_charge_status || null,
  };
}

const STATUS_CONFIG = {
  pending: {
    label: 'Pendente',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: Clock,
  },
  overdue: {
    label: 'Atrasado',
    className: 'bg-red-500 text-white border-red-500',
    icon: AlertTriangle,
  },
  paid: {
    label: 'Pago',
    className: 'bg-green-600 text-white border-green-600',
    icon: CheckCircle,
  },
  partially_paid: {
    label: 'Pago parcialmente',
    className: 'bg-amber-400 text-amber-950 border-amber-400',
    icon: DollarSign,
  },
  cancelled: {
    label: 'Cancelado',
    className: 'bg-slate-100 text-slate-500 border-slate-200',
    icon: X,
  },
};

const CANCELLATION_REASONS = [
  'Cliente solicitou cancelamento',
  'Negociação cancelada',
  'Pagamento duplicado',
  'Erro de lançamento',
  'Inadimplência irrecuperável',
  'Outro',
];

const DATE_FILTER_OPTIONS = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'this_week_plus_last_week', label: 'Esta semana + Atrasados sem. passada' },
  { value: 'last_week', label: 'Semana passada' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'next_30', label: 'Próximos 30 dias' },
  { value: 'overdue_all', label: 'Todos os atrasados' },
  { value: 'custom', label: 'Selecionar período' },
];

export default function Receivables() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Filters
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('all');
  const [brand, setBrand] = useState<Brand>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilterType>('this_month');
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('flat');
  const [sortConfig, setSortConfig] = useState<{ column: 'valor' | 'vencimento'; direction: 'asc' | 'desc' } | null>(null);
  
  // Calculate date range based on filter
  const dateRange = useMemo(() => {
    const today = getLocalDayStart(new Date());
    
    switch (dateFilter) {
      case 'today':
        return {
          from: safeFormat(startOfDay(today), 'yyyy-MM-dd'),
          to: safeFormat(endOfDay(today), 'yyyy-MM-dd'),
        };
      case 'yesterday':
        const yesterday = subDays(today, 1);
        return {
          from: safeFormat(startOfDay(yesterday), 'yyyy-MM-dd'),
          to: safeFormat(endOfDay(yesterday), 'yyyy-MM-dd'),
        };
      case 'this_week':
        return {
          from: safeFormat(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          to: safeFormat(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        };
      case 'this_week_plus_last_week':
        const lastWeekStart = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
        return {
          from: safeFormat(lastWeekStart, 'yyyy-MM-dd'),
          to: safeFormat(thisWeekEnd, 'yyyy-MM-dd'),
        };
      case 'last_week':
        const lastWeek = subWeeks(today, 1);
        return {
          from: safeFormat(startOfWeek(lastWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          to: safeFormat(endOfWeek(lastWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        };
      case 'this_month':
        return {
          from: safeFormat(startOfMonth(today), 'yyyy-MM-dd'),
          to: safeFormat(endOfMonth(today), 'yyyy-MM-dd'),
        };
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        return {
          from: safeFormat(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          to: safeFormat(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        };
      case 'next_30':
        return {
          from: safeFormat(today, 'yyyy-MM-dd'),
          to: safeFormat(addDays(today, 30), 'yyyy-MM-dd'),
        };
      case 'overdue_all':
        return {
          from: undefined,
          to: safeFormat(subDays(today, 1), 'yyyy-MM-dd'),
        };
      case 'custom':
        return {
          from: customDateRange.from ? safeFormat(getLocalDayStart(customDateRange.from), 'yyyy-MM-dd') : undefined,
          to: customDateRange.to ? safeFormat(getLocalDayStart(customDateRange.to), 'yyyy-MM-dd') : undefined,
        };
      default:
        return { from: undefined, to: undefined };
    }
  }, [dateFilter, customDateRange]);
  
  // Get label for active filter
  const dateFilterLabel = useMemo(() => {
    if (dateFilter === 'custom' && customDateRange.from && customDateRange.to) {
      return `${safeFormat(customDateRange.from, 'dd/MM/yyyy')} - ${safeFormat(customDateRange.to, 'dd/MM/yyyy')}`;
    }
    return DATE_FILTER_OPTIONS.find(o => o.value === dateFilter)?.label || 'Todos os tempos';
  }, [dateFilter, customDateRange]);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Modals
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isBulkActionDialogOpen, setIsBulkActionDialogOpen] = useState(false);
  const [isChargeDialogOpen, setIsChargeDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [isRenegotiationQueueOpen, setIsRenegotiationQueueOpen] = useState(false);
  const [registerContactPayment, setRegisterContactPayment] = useState<ReceivablePayment | null>(null);
  const [registerContactPrefill, setRegisterContactPrefill] = useState<RegisterContactPrefill | undefined>(undefined);
  const [callFallback, setCallFallback] = useState<{ payment: ReceivablePayment; phoneE164: string; reason: string } | null>(null);
  const [pendingReceivableCall, setPendingReceivableCall] = useState<PendingReceivableCall | null>(null);
  const pendingReceivableCallRef = useRef<PendingReceivableCall | null>(null);
  const wasOnCallRef = useRef(false);
  
  // Selected payment for actions
  const [selectedPayment, setSelectedPayment] = useState<ReceivablePayment | null>(null);
  const [highlightPaymentId, setHighlightPaymentId] = useState<string | null>(null);
  const [historyPayment, setHistoryPayment] = useState<ReceivablePayment | null>(null);
  const [renegotiationPayment, setRenegotiationPayment] = useState<ReceivablePayment | null>(null);
  
  // Pay modal state
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(safeFormat(new Date(), 'yyyy-MM-dd'));
  const [paymentMethod, setPaymentMethod] = useState('');
  const [payBankAccountId, setPayBankAccountId] = useState('');
  const [payNotes, setPayNotes] = useState('');
  
  // Cancel modal state
  const [cancellationReason, setCancellationReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [chargeMethod, setChargeMethod] = useState<ChargeMethod>('stripe');
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeNote, setChargeNote] = useState('');
  const [isChargingNow, setIsChargingNow] = useState(false);
  const [selectedCardInfo, setSelectedCardInfo] = useState<SelectedCardInfo | null>(null);
  const [loadingCardInfo, setLoadingCardInfo] = useState(false);
  const [manualLinkPi, setManualLinkPi] = useState('');
  const [manualLinkLoading, setManualLinkLoading] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState<HistoryStatusFilter>('all');
  const [historyWindowFilter, setHistoryWindowFilter] = useState<HistoryWindowFilter>('all');
  const { makeCall, isReady: twilioReady, isOnCall, callError } = useTwilio();
  
  // Queries & Mutations
  const { data: payments = [], isLoading, refetch } = useReceivables({ 
    businessUnit,
    brand,
    status: statusFilter === 'active' ? 'all' : statusFilter,
    dueDateFrom: dateRange.from,
    dueDateTo: dateRange.to,
    limit: 5000,
  });
  const { data: bankAccounts = [] } = useBankAccounts('all');
  const { data: pendingRenegotiations = [] } = usePaymentAgreements('pending_approval');
  
  const markAsPaid = useMarkPaymentAsPaid();
  const cancelPayment = useCancelPayment();
  const bulkUpdate = useBulkUpdatePayments();
  
  // Dynamic columns for grouped view
  const {
    groupColumns,
    visibleGroupColumns,
    groupColumnOrder,
    groupSortConfig,
    orderedVisibleGroupColumns,
    toggleGroupColumnVisibility,
    reorderGroupColumns,
    toggleGroupSort,
    paymentColumns,
    orderedVisiblePaymentColumns,
    paymentSortConfig,
    togglePaymentSort,
    resetToDefault: resetColumnsToDefault,
  } = useReceivablesGroupedColumns();

  const normalizedPayments = useMemo(
    () =>
      (Array.isArray(payments) ? payments : []).filter(
        (payment): payment is ReceivablePayment => Boolean(payment?.payment_id),
      ),
    [payments],
  );
  
  // Filtered payments by search
  const filteredPayments = useMemo(() => {
    let result = normalizedPayments;

    if (statusFilter === 'active') {
      result = result.filter((payment) => {
        const effective = getEffectivePaymentStatus({
          status: payment.status,
          dueDate: payment.due_date,
          amount: payment.amount,
          paidAmount: payment.paid_amount,
          balance: payment.valor_aberto,
        });
        return effective === 'pending' || effective === 'overdue' || effective === 'partially_paid';
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((p) =>
        p.cliente?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    if (sortConfig) {
      const { column, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        if (column === 'valor') {
          return ((a.valor_aberto || 0) - (b.valor_aberto || 0)) * dir;
        }
        const da = a.due_date ? new Date(a.due_date).getTime() : 0;
        const db = b.due_date ? new Date(b.due_date).getTime() : 0;
        return (da - db) * dir;
      });
    }

    return result;
  }, [normalizedPayments, searchQuery, statusFilter, sortConfig]);

  const filteredPaymentIds = useMemo(
    () => Array.from(new Set(filteredPayments.map((p) => p.payment_id).filter(Boolean))),
    [filteredPayments],
  );
  const opsMetaPaymentIds = useMemo(() => filteredPaymentIds.slice(0, 300), [filteredPaymentIds]);

  const { data: opsMetaByPaymentId = {} } = useQuery({
    queryKey: ['receivables-ops-meta', opsMetaPaymentIds],
    enabled: opsMetaPaymentIds.length > 0,
    queryFn: async () => {
      const paymentRows: Array<{ id: string; deal_id: string; auto_charge_enabled: boolean | null; auto_charge_status: string | null }> = [];
      for (const ids of chunkIds(opsMetaPaymentIds, 500)) {
        const { data, error } = await (supabase.from('payments') as any)
          .select('id,deal_id,auto_charge_enabled,auto_charge_status')
          .in('id', ids);
        if (error) throw error;
        paymentRows.push(...((data || []) as any[]));
      }

      const dealIds = Array.from(new Set(paymentRows.map((r) => r.deal_id).filter(Boolean)));
      const dealRows: Array<{ id: string; contact_id: string | null }> = [];
      for (const ids of chunkIds(dealIds, 500)) {
        const { data, error } = await (supabase.from('deals') as any)
          .select('id,contact_id')
          .in('id', ids);
        if (error) throw error;
        dealRows.push(...((data || []) as any[]));
      }
      const contactIdByDealId = new Map(dealRows.map((d) => [d.id, d.contact_id]));

      const contactIds = Array.from(new Set(dealRows.map((d) => d.contact_id).filter(Boolean))) as string[];
      const contactRows: Array<{
        id: string;
        has_payment_method: boolean | null;
        stripe_default_payment_method_id: string | null;
      }> = [];
      for (const ids of chunkIds(contactIds, 500)) {
        const { data, error } = await (supabase.from('contacts') as any)
          .select('id,has_payment_method,stripe_default_payment_method_id')
          .in('id', ids);
        if (error) throw error;
        contactRows.push(...((data || []) as any[]));
      }
      const hasCardByContactId = new Map(
        contactRows.map((c) => [
          c.id,
          Boolean(c.has_payment_method) || Boolean(c.stripe_default_payment_method_id),
        ]),
      );

      const result: Record<string, ReceivableOpsMeta> = {};
      for (const row of paymentRows) {
        const contactId = contactIdByDealId.get(row.deal_id) || null;
        result[row.id] = {
          contactId,
          hasCard: contactId ? Boolean(hasCardByContactId.get(contactId)) : false,
          autoChargeEnabled: Boolean(row.auto_charge_enabled),
          autoChargeStatus: row.auto_charge_status || null,
        };
      }
      return result;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: paymentTxStatsByPaymentId = {} } = useQuery({
    queryKey: ['receivables-payment-tx-stats', filteredPaymentIds],
    enabled: filteredPaymentIds.length > 0,
    queryFn: async () => {
      const txRows: any[] = [];
      for (const ids of chunkIds(filteredPaymentIds, 500)) {
        const { data, error } = await (supabase.from('payment_transactions') as any)
          .select('payment_id,status,payment_method,created_at,stripe_payment_intent_id')
          .in('payment_id', ids)
          .order('created_at', { ascending: false });
        if (error) throw error;
        txRows.push(...(data || []));
      }

      const byPayment: Record<string, PaymentTxStats> = {};
      for (const row of txRows) {
        const paymentId = row.payment_id as string | null;
        if (!paymentId) continue;
        if (!byPayment[paymentId]) {
          byPayment[paymentId] = {
            totalAttempts: 0,
            successCount: 0,
            failedCount: 0,
            lastAt: null,
            lastStatus: null,
            lastMethod: null,
            lastPi: null,
          };
        }
        const entry = byPayment[paymentId];
        entry.totalAttempts += 1;
        const status = String(row.status || '').toLowerCase();
        if (status === 'confirmed') entry.successCount += 1;
        if (status === 'rejected' || status === 'failed') entry.failedCount += 1;
        if (!entry.lastAt) {
          entry.lastAt = row.created_at || null;
          entry.lastStatus = row.status || null;
          entry.lastMethod = row.payment_method || null;
          entry.lastPi = row.stripe_payment_intent_id || null;
        }
      }

      return byPayment;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: paymentHistoryRows = [], refetch: refetchPaymentHistory } = useQuery({
    queryKey: ['receivables-payment-history', historyPayment?.payment_id],
    enabled: Boolean(isHistoryDialogOpen && historyPayment?.payment_id),
    queryFn: async () => {
      const { data, error } = await (supabase.from('payment_transactions') as any)
        .select('id,created_at,transaction_date,transaction_type,payment_method,status,amount,currency,error_code,reference_number,stripe_payment_intent_id,notes,confirmed_at')
        .eq('payment_id', historyPayment!.payment_id)
        .order('created_at', { ascending: false })
        .limit(80);
      if (error) throw error;
      return (data || []) as PaymentHistoryRow[];
    },
    staleTime: 5_000,
    refetchOnWindowFocus: false,
  });

  const filteredPaymentHistoryRows = useMemo(() => {
    const now = Date.now();
    return (Array.isArray(paymentHistoryRows) ? paymentHistoryRows : []).filter((row): row is PaymentHistoryRow => {
      if (!row?.id) return false;
      const statusCategory = normalizeHistoryStatus(row.status);
      if (historyStatusFilter === 'success' && statusCategory !== 'success') return false;
      if (historyStatusFilter === 'failed' && statusCategory !== 'failed') return false;
      if (historyStatusFilter === 'pending' && statusCategory !== 'pending') return false;

      if (historyWindowFilter === 'all') return true;
      const sourceDate = row.created_at || row.transaction_date;
      if (!sourceDate) return false;
      const rowTime = new Date(sourceDate).getTime();
      if (Number.isNaN(rowTime)) return false;
      const days = historyWindowFilter === '7d' ? 7 : historyWindowFilter === '30d' ? 30 : 90;
      const threshold = now - (days * 24 * 60 * 60 * 1000);
      return rowTime >= threshold;
    });
  }, [paymentHistoryRows, historyStatusFilter, historyWindowFilter]);
  
  // Helper function to compare values for sorting
  const compareValues = (a: unknown, b: unknown, direction: 'asc' | 'desc'): number => {
    const multiplier = direction === 'asc' ? 1 : -1;
    
    if (typeof a === 'number' && typeof b === 'number') {
      return (a - b) * multiplier;
    }
    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b, 'pt-BR') * multiplier;
    }
    return 0;
  };

  // Grouped by client (base, sem sort)
  const groupedByClientBase = useMemo((): ClientGroup[] => {
    const groups = new Map<string, ClientGroup>();
    
    filteredPayments.forEach(payment => {
      const key = payment.contact_id;
      const existing = groups.get(key) || {
        clientName: payment.cliente,
        contactId: payment.contact_id,
        payments: [],
        totalAberto: 0,
        totalAtrasado: 0,
        quantidadePagamentos: 0,
        maiorAtraso: 0,
      };
      
      existing.payments.push(payment);
      existing.totalAberto += payment.valor_aberto;
      existing.quantidadePagamentos += 1;
      
      if (payment.dias_atraso > 0) {
        existing.totalAtrasado += payment.valor_aberto;
      }
      if (payment.dias_atraso > existing.maiorAtraso) {
        existing.maiorAtraso = payment.dias_atraso;
      }
      
      groups.set(key, existing);
    });
    
    return Array.from(groups.values());
  }, [filteredPayments]);

  // Sorted groups
  const groupedByClient = useMemo((): ClientGroup[] => {
    const sorted = [...groupedByClientBase];
    
    if (groupSortConfig) {
      sorted.sort((a, b) => {
        const aValue = a[groupSortConfig.column as keyof ClientGroup];
        const bValue = b[groupSortConfig.column as keyof ClientGroup];
        return compareValues(aValue, bValue, groupSortConfig.direction);
      });
    } else {
      // Default: ordenar por totalAtrasado desc
      sorted.sort((a, b) => b.totalAtrasado - a.totalAtrasado);
    }
    
    return sorted;
  }, [groupedByClientBase, groupSortConfig]);
  
  // Stats calculados sobre o conjunto atual já filtrado
  const stats = useMemo(() => {
    const visiblePayments = filteredPayments;

    const pending = visiblePayments
      .filter((p) => {
        const effective = getEffectivePaymentStatus({
          status: p.status,
          dueDate: p.due_date,
          amount: p.amount,
          paidAmount: p.paid_amount,
          balance: p.valor_aberto,
        });
        return effective !== 'paid' && effective !== 'cancelled' && p.dias_atraso <= 0;
      })
      .reduce((sum, p) => sum + (p.valor_aberto || 0), 0);
      
    const overdue = visiblePayments
      .filter((p) => {
        const effective = getEffectivePaymentStatus({
          status: p.status,
          dueDate: p.due_date,
          amount: p.amount,
          paidAmount: p.paid_amount,
          balance: p.valor_aberto,
        });
        return effective !== 'paid' && effective !== 'cancelled' && p.dias_atraso > 0;
      })
      .reduce((sum, p) => sum + (p.valor_aberto || 0), 0);

    const paid = visiblePayments
      .filter((p) => getEffectivePaymentStatus({
        status: p.status,
        dueDate: p.due_date,
        amount: p.amount,
        paidAmount: p.paid_amount,
        balance: p.valor_aberto,
      }) === 'paid')
      .reduce((sum, p) => sum + (p.paid_amount || 0), 0);

    const total = visiblePayments.reduce((sum, p) => {
      const effective = getEffectivePaymentStatus({
        status: p.status,
        dueDate: p.due_date,
        amount: p.amount,
        paidAmount: p.paid_amount,
        balance: p.valor_aberto,
      });
      if (effective === 'paid') return sum + (p.paid_amount || 0);
      return sum + (p.valor_aberto || 0);
    }, 0);
    
    return { 
      total,
      pending,
      overdue,
      paid,
      totalRegistros: visiblePayments.length,
    };
  }, [filteredPayments]);

  // Estatísticas da tabela atual (registros exibidos)
  const tableStats = useMemo(() => {
    const uniqueClients = new Set(filteredPayments.map(p => p.contact_id)).size;
    const uniqueDeals = new Set(filteredPayments.map(p => p.deal_id)).size;
    const uniqueSellers = new Set(filteredPayments.filter(p => p.vendedor).map(p => p.vendedor)).size;
    const totalValue = filteredPayments.reduce((sum, p) => sum + (p.valor_aberto || 0), 0);
    
    return {
      registros: filteredPayments.length,
      clientes: uniqueClients,
      deals: uniqueDeals,
      vendedores: uniqueSellers,
      valorTotal: totalValue,
    };
  }, [filteredPayments]);
  
  // Selection handlers
  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPayments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPayments.map(p => p.payment_id)));
    }
  };
  
  const toggleSelectGroup = (paymentIds: string[]) => {
    const newSet = new Set(selectedIds);
    const allSelected = paymentIds.every(id => newSet.has(id));
    
    if (allSelected) {
      // Deselecionar todos do grupo
      paymentIds.forEach(id => newSet.delete(id));
    } else {
      // Selecionar todos do grupo
      paymentIds.forEach(id => newSet.add(id));
    }
    setSelectedIds(newSet);
  };
  
  // Action handlers
  const handleOpenPay = (payment: ReceivablePayment) => {
    setSelectedPayment(payment);
    setPayAmount(payment.valor_aberto.toString());
    setPayDate(safeFormat(new Date(), 'yyyy-MM-dd'));
    setPayBankAccountId('');
    setPaymentMethod('');
    setPayNotes('');
    setIsPayModalOpen(true);
  };

  const handleOpenCharge = (payment: ReceivablePayment) => {
    setSelectedPayment(payment);
    setChargeMethod('stripe');
    setChargeAmount((payment.valor_aberto || 0).toFixed(2));
    setChargeNote('');
    setIsChargeDialogOpen(true);
  };

  const handleOpenHistory = (payment: ReceivablePayment) => {
    setHistoryPayment(payment);
    setManualLinkPi('');
    setHistoryStatusFilter('all');
    setHistoryWindowFilter('all');
    setIsHistoryDialogOpen(true);
  };

  const handleOpenRenegotiation = (payment: ReceivablePayment) => {
    setRenegotiationPayment(payment);
  };

  const openRegisterContactModal = (payment: ReceivablePayment, prefill?: RegisterContactPrefill) => {
    setRegisterContactPayment(payment);
    setRegisterContactPrefill(prefill);
  };

  const resolveContactPhoneE164 = async (contactId: string): Promise<string | null> => {
    const { data, error } = await (supabase.from('contacts') as any)
      .select('primary_phone,primary_phone_country_code')
      .eq('id', contactId)
      .maybeSingle();
    if (error) throw error;

    const phone = data?.primary_phone as string | null;
    const countryCode = data?.primary_phone_country_code as string | null;
    if (!phone) return null;
    return getFullPhoneE164(phone, countryCode || undefined);
  };

  const openCallFallback = (payment: ReceivablePayment, phoneE164: string, reason: string) => {
    setCallFallback({ payment, phoneE164, reason });
  };

  const handleStartInternalCall = async (payment: ReceivablePayment) => {
    try {
      const phoneE164 = await resolveContactPhoneE164(payment.contact_id);
      if (!phoneE164) {
        toast.error('Cliente sem telefone principal para ligação');
        openRegisterContactModal(payment, { contact_type: 'call', reason: 'collection' });
        return;
      }

      if (!twilioReady) {
        openCallFallback(
          payment,
          phoneE164,
          'Discador interno indisponível no momento. Você pode ligar pelo telefone e registrar o resultado normalmente.',
        );
        return;
      }

      await makeCall(phoneE164, payment.contact_id);
      setPendingReceivableCall({ payment, phoneE164, wasConnected: false });
      toast.success('Ligação iniciada');
    } catch (error) {
      console.error('Error starting internal receivables call:', error);
      let fallbackPhone = '';
      try {
        fallbackPhone = (await resolveContactPhoneE164(payment.contact_id)) || '';
      } catch (resolveError) {
        console.error('Error resolving fallback phone:', resolveError);
      }

      if (fallbackPhone) {
        openCallFallback(
          payment,
          fallbackPhone,
          'Não foi possível iniciar a ligação interna. Use o discador do telefone e registre o resultado em seguida.',
        );
      } else {
        toast.error('Falha ao iniciar ligação interna e telefone não encontrado');
        openRegisterContactModal(payment, { contact_type: 'call', reason: 'collection' });
      }
    }
  };
  
  const handleOpenCancel = (payment: ReceivablePayment) => {
    setSelectedPayment(payment);
    setCancellationReason('');
    setCustomReason('');
    setIsCancelDialogOpen(true);
  };
  
  const handleSubmitPay = async () => {
    if (!selectedPayment || !payAmount || !payDate) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    const method = String(paymentMethod || '').toLowerCase();
    const requiresBankAccount = method !== 'cash';
    if (!paymentMethod) {
      toast.error('Selecione o método de pagamento');
      return;
    }
    if (requiresBankAccount && !payBankAccountId) {
      toast.error('Selecione a conta bancária do recebimento');
      return;
    }
    
    try {
      await markAsPaid.mutateAsync({
        paymentId: selectedPayment.payment_id,
        paidAmount: parseFloat(payAmount),
        paidAt: payDate,
        paymentMethod,
        bankAccountId: payBankAccountId || undefined,
        notes: payNotes || undefined,
      });
      toast.success('Pagamento registrado com sucesso');
      setIsPayModalOpen(false);
    } catch (error: any) {
      const message = String(error?.message || 'Erro ao registrar pagamento');
      toast.error(message);
      console.error(error);
    }
  };
  
  const handleConfirmCancel = async () => {
    if (!selectedPayment) return;
    
    const reason = cancellationReason === 'Outro' ? customReason : cancellationReason;
    
    if (!reason) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }
    
    try {
      await cancelPayment.mutateAsync({
        paymentId: selectedPayment.payment_id,
        cancellationReason: reason,
      });
      toast.success('Pagamento cancelado');
      setIsCancelDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao cancelar pagamento');
      console.error(error);
    }
  };

  const handleRegisterCharge = async () => {
    if (!selectedPayment) return;
    const openBalance = Number(selectedPayment.valor_aberto || 0);
    const rawChargeAmount = String(chargeAmount || '').replace(',', '.').trim();
    const parsedChargeAmount = rawChargeAmount ? Number(rawChargeAmount) : openBalance;
    const chargeAmountToUse = Number.isFinite(parsedChargeAmount) ? parsedChargeAmount : NaN;

    if (chargeMethod === 'stripe') {
      if (!Number.isFinite(chargeAmountToUse) || chargeAmountToUse <= 0) {
        toast.error('Informe um valor de cobrança válido');
        return;
      }
      if (chargeAmountToUse > openBalance) {
        toast.error('Valor de cobrança não pode ser maior que o saldo em aberto');
        return;
      }
    }

    let opsMeta = opsMetaByPaymentId[selectedPayment.payment_id] || null;
    if (!opsMeta) {
      try {
        opsMeta = await fetchPaymentOpsMeta(selectedPayment.payment_id);
      } catch (error) {
        console.warn('fetchPaymentOpsMeta failed:', error);
      }
    }
    try {
      setIsChargingNow(true);

      const now = new Date().toISOString();
      const existing = selectedPayment.description || '';
      const note = chargeNote.trim()
        ? `${existing}\n[Cobrança ${chargeMethod.toUpperCase()} em ${new Date().toLocaleString('pt-BR')}] ${chargeNote.trim()}`
        : `${existing}\n[Cobrança ${chargeMethod.toUpperCase()} em ${new Date().toLocaleString('pt-BR')}]`;

      const payload: Record<string, any> = {
        sent_at: now,
        notes: note.trim(),
      };
    if (selectedPayment?.status === 'pending' || selectedPayment?.status === 'overdue') {
      payload.collection_status = 'notified';
    }

      const { error } = await supabase
        .from('payments')
        .update(payload)
        .eq('id', selectedPayment.payment_id);
      if (error) throw error;

      if (chargeMethod === 'stripe') {
        // Source of truth: try direct saved-card charge first.
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error: chargeError } = await supabase.functions.invoke('stripe-charge-saved-card', {
          body: {
            payment_id: selectedPayment.payment_id,
            force: true,
            triggered_by: user?.id || null,
            trigger_type: 'manual',
            charge_amount: chargeAmountToUse,
          },
        });

        const chargeSucceeded = !chargeError && Boolean(data?.success);
        if (chargeSucceeded) {
          toast.success('Cobrança no cartão realizada com sucesso');
        } else {
          // If no saved card, fallback to payment link.
          const msg = String(chargeError?.message || data?.message || data?.error || '').toLowerCase();
          const canFallbackToLink =
            msg.includes('no card') ||
            msg.includes('payment method') ||
            msg.includes('cartão') ||
            msg.includes('metodo');

          if (!canFallbackToLink) {
            throw chargeError || new Error(data?.message || data?.error || 'Falha ao cobrar cartão salvo');
          }

          if (!opsMeta?.contactId) {
            throw new Error('Contato não identificado para gerar link de pagamento');
          }

          const { data: linkData, error: linkError } = await supabase.functions.invoke('stripe-create-payment-link', {
            body: {
              contact_id: opsMeta.contactId,
              payment_id: selectedPayment.payment_id,
              amount: chargeAmountToUse,
              origin_context: 'finance_receivables_fallback',
              mode: 'payment',
              success_url: window.location.href,
              cancel_url: window.location.href,
            },
          });
          if (linkError) throw linkError;
          if (!linkData?.url) throw new Error('Link de pagamento não retornado');
          window.open(linkData.url, '_blank');
          toast.success('Cliente sem cartão padrão. Link de pagamento gerado');
        }
      } else {
        toast.success('Cobrança registrada com sucesso');
      }

      setIsChargeDialogOpen(false);
      await refetch();
    } catch (error: any) {
      console.error(error);
      toast.error(getChargeErrorMessage(error));
    } finally {
      setIsChargingNow(false);
    }
  };

  const handleManualLinkStripePi = async () => {
    if (!historyPayment) return;
    const pi = manualLinkPi.trim();
    if (!pi || !pi.startsWith('pi_')) {
      toast.error('Informe um PaymentIntent válido (pi_...)');
      return;
    }
    try {
      setManualLinkLoading(true);
      const { data, error } = await supabase.functions.invoke('stripe-backfill-payment-intent', {
        body: {
          payment_intent_id: pi,
          payment_id: historyPayment.payment_id,
        },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || data?.message || 'Falha ao vincular');
      toast.success('PaymentIntent vinculado ao pagamento com sucesso');
      setManualLinkPi('');
      await Promise.all([refetchPaymentHistory(), refetch()]);
    } catch (error: any) {
      console.error(error);
      toast.error(getChargeErrorMessage(error));
    } finally {
      setManualLinkLoading(false);
    }
  };

  useEffect(() => {
    pendingReceivableCallRef.current = pendingReceivableCall;
  }, [pendingReceivableCall]);

  useEffect(() => {
    if (isOnCall && pendingReceivableCallRef.current && !pendingReceivableCallRef.current.wasConnected) {
      setPendingReceivableCall((current) => (current ? { ...current, wasConnected: true } : current));
    }

    const wasOnCall = wasOnCallRef.current;
    wasOnCallRef.current = isOnCall;

    if (wasOnCall && !isOnCall) {
      const flow = pendingReceivableCallRef.current;
      if (flow?.wasConnected) {
        openRegisterContactModal(flow.payment, { contact_type: 'call', reason: 'collection' });
        setPendingReceivableCall(null);
        toast.info('Ligação finalizada. Registre o resultado da cobrança.');
      }
    }
  }, [isOnCall]);

  useEffect(() => {
    if (!callError) return;
    const flow = pendingReceivableCallRef.current;
    if (!flow || flow.wasConnected) return;

    setPendingReceivableCall(null);
    openCallFallback(
      flow.payment,
      flow.phoneE164,
      'A ligação interna falhou antes de conectar. Você pode ligar pelo telefone e registrar o contato.',
    );
  }, [callError]);

  useEffect(() => {
    const loadCardInfo = async () => {
      if (!isChargeDialogOpen || !selectedPayment || chargeMethod !== 'stripe') {
        setSelectedCardInfo(null);
        return;
      }

      setLoadingCardInfo(true);
      try {
        let opsMeta = opsMetaByPaymentId[selectedPayment.payment_id] || null;
        if (!opsMeta) {
          opsMeta = await fetchPaymentOpsMeta(selectedPayment.payment_id);
        }
        if (!opsMeta?.contactId) {
          setSelectedCardInfo({ brand: null, last4: null, hasSavedCard: false });
          return;
        }

        const { data, error } = await supabase.functions.invoke('stripe-list-payment-methods', {
          body: { contact_id: opsMeta.contactId },
        });
        if (error) throw error;

        const methods = Array.isArray(data?.payment_methods) ? data.payment_methods : [];
        const preferred = methods.find((m: any) => Boolean(m?.is_default)) || methods[0] || null;
        setSelectedCardInfo({
          brand: preferred?.card?.brand || null,
          last4: preferred?.card?.last4 || null,
          hasSavedCard: Boolean(preferred?.id),
        });
      } catch (error) {
        console.warn('loadCardInfo failed:', error);
        setSelectedCardInfo({ brand: null, last4: null, hasSavedCard: false });
      } finally {
        setLoadingCardInfo(false);
      }
    };
    loadCardInfo();
  }, [isChargeDialogOpen, selectedPayment, chargeMethod, opsMetaByPaymentId]);

  // Track focus_payment IDs we already auto-expanded for, to avoid loops
  const [autoExpandedFocusIds] = useState<Set<string>>(() => new Set<string>());

  useEffect(() => {
    const focusPaymentId = searchParams.get('focus_payment');
    const action = searchParams.get('action');
    if (!focusPaymentId) return;

    // Wait until the query has produced data at least once
    if (isLoading) return;

    const target = filteredPayments.find((p) => p.payment_id === focusPaymentId);

    if (!target) {
      // Pagamento não está no filtro atual. Tentar expandir uma única vez.
      if (!autoExpandedFocusIds.has(focusPaymentId) && dateFilter !== 'overdue_all') {
        autoExpandedFocusIds.add(focusPaymentId);
        setDateFilter('overdue_all');
        toast.message('Ajustando filtro para localizar o pagamento solicitado…');
        return; // próximo ciclo, com novo filteredPayments, vai achar
      }

      // Já tentamos expandir e ainda não achou — limpar params para não travar a URL
      toast.error(
        'Pagamento não encontrado neste contexto. Verifique se foi cancelado ou se você tem permissão.',
      );
      const cleanup = new URLSearchParams(searchParams);
      cleanup.delete('focus_payment');
      cleanup.delete('action');
      cleanup.delete('origin');
      cleanup.delete('deal_id');
      setSearchParams(cleanup, { replace: true });
      return;
    }

    setHighlightPaymentId(target.payment_id);
    if (action === 'charge') handleOpenCharge(target);
    if (action === 'receive') handleOpenPay(target);

    const next = new URLSearchParams(searchParams);
    next.delete('focus_payment');
    next.delete('action');
    next.delete('origin');
    next.delete('deal_id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, filteredPayments, isLoading, dateFilter, autoExpandedFocusIds]);

  
  const handleBulkMarkAsPaid = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      // Buscar app_user_id do usuário logado
      const { data: { user } } = await supabase.auth.getUser();
      let appUserId: string | null = null;
      
      if (user) {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();
        appUserId = appUser?.id ?? null;
      }
      
      await bulkUpdate.mutateAsync({
        paymentIds: Array.from(selectedIds),
        updates: { 
          status: 'paid',
          paid_at: new Date().toISOString(),
          paid_by: appUserId,
        },
      });
      toast.success(`${selectedIds.size} pagamentos marcados como pagos`);
      setSelectedIds(new Set());
      setIsBulkActionDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao atualizar pagamentos');
      console.error(error);
    }
  };
  
  const getPaymentStatus = (payment: ReceivablePayment): keyof typeof STATUS_CONFIG => {
    const effective = getEffectivePaymentStatus({
      status: payment.status,
      dueDate: payment.due_date,
      amount: payment.amount,
      paidAmount: payment.paid_amount,
      balance: payment.valor_aberto,
    });
    if (effective === 'paid' || effective === 'cancelled' || effective === 'overdue' || effective === 'partially_paid') return effective;
    if (payment.dias_atraso > 0) return 'overdue';
    return 'pending';
  };

  return (
    <FinancePageLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold text-foreground">Contas a Receber</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setIsRenegotiationQueueOpen(true)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Fila Renegociação
              <Badge variant="secondary" className="ml-2">
                {pendingRenegotiations.length}
              </Badge>
            </Button>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>
        
        {/* Stats Cards - Valores filtrados pelo período */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total a Receber
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-8 w-32 bg-muted animate-pulse rounded" />
              ) : (
                <div className="text-2xl font-bold">
                  <MoneyDisplay value={stats.total} currency="USD" />
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{dateFilterLabel}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                A Vencer
              </CardTitle>
              <Clock className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-8 w-32 bg-muted animate-pulse rounded" />
              ) : (
                <div className="text-2xl font-bold text-blue-600">
                  <MoneyDisplay value={stats.pending} currency="USD" />
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{dateFilterLabel}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Em Atraso
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-8 w-32 bg-muted animate-pulse rounded" />
              ) : (
                <div className="text-2xl font-bold text-destructive">
                  <MoneyDisplay value={stats.overdue} currency="USD" />
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{dateFilterLabel}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Recebido
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-8 w-32 bg-muted animate-pulse rounded" />
              ) : (
                <div className="text-2xl font-bold text-emerald-600">
                  <MoneyDisplay value={stats.paid} currency="USD" />
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{dateFilterLabel}</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label>Business Unit</Label>
                <BusinessUnitSelector
                  value={businessUnit}
                  onChange={setBusinessUnit}
                  includeAll
                />
              </div>
              
              <div className="space-y-2">
                <Label>Marca</Label>
                <BrandSelector
                  value={brand}
                  onChange={setBrand}
                  includeAll
                />
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">A Vencer + Atrasados</SelectItem>
                    <SelectItem value="pending">A Vencer</SelectItem>
                    <SelectItem value="overdue">Atrasados</SelectItem>
                    <SelectItem value="paid">Pagos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Período</Label>
                <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as DateFilterType)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FILTER_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {dateFilter === 'custom' && (
                <div className="space-y-2">
                  <Label>De / Até</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[240px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customDateRange.from && customDateRange.to ? (
                          <>
                            {safeFormat(customDateRange.from, 'dd/MM/yy')} - {safeFormat(customDateRange.to, 'dd/MM/yy')}
                          </>
                        ) : (
                          <span className="text-muted-foreground">Selecione o período</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="range"
                        selected={{ from: customDateRange.from, to: customDateRange.to }}
                        onSelect={(range) => setCustomDateRange({ from: range?.from, to: range?.to })}
                        numberOfMonths={2}
                        locale={ptBR}
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              
              <div className="space-y-2 flex-1 min-w-[200px]">
                <Label>Buscar</Label>
                <Input
                  placeholder="Cliente ou descrição..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Visualização</Label>
                <ToggleGroup 
                  type="single" 
                  value={viewMode} 
                  onValueChange={(v) => v && setViewMode(v as ViewMode)}
                  className="justify-start"
                >
                  <ToggleGroupItem value="flat" aria-label="Lista completa">
                    <List className="h-4 w-4 mr-1" />
                    Lista
                  </ToggleGroupItem>
                  <ToggleGroupItem value="grouped" aria-label="Por cliente">
                    <Users className="h-4 w-4 mr-1" />
                    Por Cliente
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              
              {/* Column Manager for grouped view */}
              {viewMode === 'grouped' && (
                <div className="space-y-2">
                  <Label>Colunas</Label>
                  <ColumnManager
                    columns={groupColumns}
                    columnOrder={groupColumnOrder}
                    visibleColumns={visibleGroupColumns}
                    onToggleVisibility={toggleGroupColumnVisibility}
                    onReorder={reorderGroupColumns}
                    onReset={resetColumnsToDefault}
                  />
                </div>
              )}
            </div>
            
            {/* Bulk Actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size} selecionados
                </span>
                <Button size="sm" onClick={() => setIsBulkActionDialogOpen(true)}>
                  <Check className="h-4 w-4 mr-2" />
                  Marcar como Pago
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setSelectedIds(new Set())}
                >
                  Limpar Seleção
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Payments Table */}
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum pagamento encontrado
              </div>
            ) : viewMode === 'grouped' ? (
              <GroupedByClientTable
                groups={groupedByClient}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectGroup={toggleSelectGroup}
                onOpenPay={handleOpenPay}
                onOpenCancel={handleOpenCancel}
                onOpenCharge={handleOpenCharge}
                groupColumns={orderedVisibleGroupColumns}
                paymentColumns={orderedVisiblePaymentColumns}
                groupSortConfig={groupSortConfig}
                paymentSortConfig={paymentSortConfig}
                onGroupSort={toggleGroupSort}
                onPaymentSort={togglePaymentSort}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedIds.size === filteredPayments.length && filteredPayments.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>
                      <button
                        type="button"
                        onClick={() => setSortConfig((prev) => {
                          if (prev?.column !== 'valor') return { column: 'valor', direction: 'asc' };
                          if (prev.direction === 'asc') return { column: 'valor', direction: 'desc' };
                          return null;
                        })}
                        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      >
                        Valor
                        {sortConfig?.column === 'valor' ? (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        onClick={() => setSortConfig((prev) => {
                          if (prev?.column !== 'vencimento') return { column: 'vencimento', direction: 'asc' };
                          if (prev.direction === 'asc') return { column: 'vencimento', direction: 'desc' };
                          return null;
                        })}
                        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      >
                        Vencimento
                        {sortConfig?.column === 'vencimento' ? (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((payment) => {
                    const status = getPaymentStatus(payment);
                    const config = STATUS_CONFIG[status];
                    const StatusIcon = config.icon;
                    
                    return (
                      <TableRow key={payment.payment_id} className={highlightPaymentId === payment.payment_id ? 'bg-amber-50' : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(payment.payment_id)}
                            onCheckedChange={() => toggleSelect(payment.payment_id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <Link 
                            to={`/deal-detail?id=${payment.deal_id}`}
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {payment.cliente}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {payment.description}
                          </div>
                          {(() => {
                            const meta = opsMetaByPaymentId[payment.payment_id];
                            const txStats = paymentTxStatsByPaymentId[payment.payment_id];
                            if (!meta) return null;
                            return (
                              <div className="mt-1 flex flex-wrap gap-1">
                                <Badge variant={meta.contactId ? 'default' : 'destructive'} className="text-[10px] px-1.5 py-0">
                                  {meta.contactId ? 'Contato OK' : 'Sem contato'}
                                </Badge>
                                <Badge variant={meta.hasCard ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                                  {meta.hasCard ? 'Cartão salvo' : 'Sem cartão'}
                                </Badge>
                                <Badge variant={meta.autoChargeEnabled ? 'default' : 'outline'} className="text-[10px] px-1.5 py-0">
                                  {meta.autoChargeEnabled ? 'Auto charge ON' : 'Auto charge OFF'}
                                </Badge>
                                {txStats ? (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                    Tentativas: {txStats.totalAttempts}
                                  </Badge>
                                ) : null}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <MoneyDisplay 
                            value={payment.valor_aberto} 
                            currency={payment.currency as 'USD' | 'BRL'} 
                          />
                        </TableCell>
                        <TableCell>
                          {safeFormat(parseDate(payment.due_date)!, 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          <Badge className={`gap-1 border ${config.className}`}>
                            <StatusIcon className="h-3 w-3" />
                            {config.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {status !== 'paid' && status !== 'cancelled' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleOpenCharge(payment)}>
                                    <DollarSign className="h-4 w-4 mr-2" />
                                    Cobrar Cliente
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleStartInternalCall(payment)}>
                                    <Phone className="h-4 w-4 mr-2" />
                                    Ligar
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleOpenPay(payment)}>
                                    <Check className="h-4 w-4 mr-2" />
                                    Registrar Pagamento
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleOpenCancel(payment)}
                                    className="text-destructive"
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Cancelar
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleOpenRenegotiation(payment)}>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Solicitar Renegociação
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleOpenHistory(payment)}>
                                <Clock className="h-4 w-4 mr-2" />
                                Histórico / Vincular PI
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/deal-detail?id=${payment.deal_id}`}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Ver Deal
                                </Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            
            {/* Rodapé com estatísticas */}
            {!isLoading && filteredPayments.length > 0 && (
              <div className="mt-4 pt-4 border-t flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">{tableStats.registros.toLocaleString('pt-BR')}</span> registros
                </div>
                <div>
                  <span className="font-medium text-foreground">{tableStats.clientes.toLocaleString('pt-BR')}</span> clientes
                </div>
                <div>
                  <span className="font-medium text-foreground">{tableStats.deals.toLocaleString('pt-BR')}</span> deals
                </div>
                <div className="ml-auto">
                  Total: <MoneyDisplay value={tableStats.valorTotal} currency="BRL" className="font-medium text-foreground" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Pay Modal */}
        <Dialog open={isPayModalOpen} onOpenChange={setIsPayModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Pagamento</DialogTitle>
              <DialogDescription>
                {selectedPayment?.cliente} - {selectedPayment?.description}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Valor Original</Label>
                <div className="text-lg font-medium">
                  <MoneyDisplay 
                    value={selectedPayment?.valor_aberto || 0} 
                    currency={(selectedPayment?.currency as 'USD' | 'BRL') || 'USD'} 
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Valor Pago *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Deixe o valor original para pagamento total ou ajuste para pagamento parcial
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Data do Pagamento *</Label>
                <Input 
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Método de Pagamento</Label>
                <Select value={paymentMethod} onValueChange={(v) => {
                  setPaymentMethod(v);
                  // Reset bank account if it doesn't match the new method's currency filter
                  const currencyFilter = v === 'pix' ? 'BRL' : ['zelle', 'ach', 'wire'].includes(v) ? 'USD' : null;
                  if (currencyFilter && payBankAccountId) {
                    const selected = bankAccounts.find((a) => a.id === payBankAccountId);
                    if (selected && selected.currency !== currencyFilter) setPayBankAccountId('');
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RECEIVABLES_MANUAL_METHOD_OPTIONS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>
                  Conta Bancária
                  {paymentMethod !== 'cash' ? ' *' : ' (opcional para Dinheiro)'}
                </Label>
                <Select value={payBankAccountId} onValueChange={setPayBankAccountId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts
                      .filter((acc) => {
                        if (paymentMethod === 'pix') return acc.currency === 'BRL';
                        if (['zelle', 'ach', 'wire'].includes(paymentMethod)) return acc.currency === 'USD';
                        if (['cash', 'check'].includes(paymentMethod)) return true; // all accounts
                        // no method selected → filter by business_unit
                        if (selectedPayment && acc.business_unit !== selectedPayment.business_unit) return false;
                        return true;
                      })
                      .map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.name} ({acc.currency})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  value={payNotes}
                  onChange={(e) => setPayNotes(e.target.value)}
                  placeholder="Notas sobre o pagamento..."
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPayModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmitPay}
                disabled={markAsPaid.isPending || !paymentMethod || (paymentMethod !== 'cash' && !payBankAccountId)}
              >
                {markAsPaid.isPending && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                Confirmar Pagamento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Histórico de Transações do Pagamento</DialogTitle>
              <DialogDescription>
                {historyPayment?.cliente} - {historyPayment?.description}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-2 py-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="rounded-md border p-2 text-sm">
                  <p className="text-muted-foreground">Tentativas totais</p>
                  <p className="font-semibold">{filteredPaymentHistoryRows.length}</p>
                </div>
                <div className="rounded-md border p-2 text-sm">
                  <p className="text-muted-foreground">Sucesso</p>
                  <p className="font-semibold text-emerald-600">
                    {filteredPaymentHistoryRows.filter((r) => normalizeHistoryStatus(r.status) === 'success').length}
                  </p>
                </div>
                <div className="rounded-md border p-2 text-sm">
                  <p className="text-muted-foreground">Falhas</p>
                  <p className="font-semibold text-destructive">
                    {filteredPaymentHistoryRows.filter((r) => normalizeHistoryStatus(r.status) === 'failed').length}
                  </p>
                </div>
              </div>

              <div className="rounded-md border p-3">
                <Label>Fallback: Vincular PaymentIntent Stripe manualmente</Label>
                <div className="mt-2 flex flex-col md:flex-row gap-2">
                  <Input
                    value={manualLinkPi}
                    onChange={(e) => setManualLinkPi(e.target.value)}
                    placeholder="pi_3T7OaOB1yuQk1MYU1xrkZvHa"
                  />
                  <Button onClick={handleManualLinkStripePi} disabled={manualLinkLoading}>
                    {manualLinkLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Vincular PI
                  </Button>
                </div>
              </div>

              <div className="max-h-[360px] overflow-auto rounded-md border">
                <div className="flex flex-col md:flex-row gap-2 p-2 border-b bg-muted/20">
                  <Select value={historyStatusFilter} onValueChange={(v: HistoryStatusFilter) => setHistoryStatusFilter(v)}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="success">Sucesso</SelectItem>
                      <SelectItem value="failed">Falhas</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={historyWindowFilter} onValueChange={(v: HistoryWindowFilter) => setHistoryWindowFilter(v)}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Período" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todo período</SelectItem>
                      <SelectItem value="7d">Últimos 7 dias</SelectItem>
                      <SelectItem value="30d">Últimos 30 dias</SelectItem>
                      <SelectItem value="90d">Últimos 90 dias</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>PI / Erro</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPaymentHistoryRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          Nenhuma transação registrada para este pagamento.
                        </TableCell>
                      </TableRow>
                    ) : filteredPaymentHistoryRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="text-xs">
                          {row.created_at ? safeFormat(new Date(row.created_at), 'dd/MM/yyyy HH:mm') : '-'}
                        </TableCell>
                        <TableCell className="text-xs">{row.transaction_type || '-'}</TableCell>
                        <TableCell className="text-xs">{row.payment_method || '-'}</TableCell>
                        <TableCell className="text-xs">{getHistoryStatusLabel(row.status)}</TableCell>
                        <TableCell className="text-xs">
                          {typeof row.amount === 'number'
                            ? formatMoney(row.amount, ((row.currency || 'USD') as 'USD' | 'BRL'))
                            : '-'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.stripe_payment_intent_id || row.error_code || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsHistoryDialogOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isRenegotiationQueueOpen} onOpenChange={setIsRenegotiationQueueOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>Fila de Aprovação de Renegociação</DialogTitle>
              <DialogDescription>
                Aprovação centralizada das solicitações pendentes.
              </DialogDescription>
            </DialogHeader>
            <RenegotiationApprovalQueue />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRenegotiationQueueOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RequestRenegotiationModal
          open={Boolean(renegotiationPayment)}
          onClose={() => setRenegotiationPayment(null)}
          dealId={renegotiationPayment?.deal_id || ''}
          clientName={renegotiationPayment?.cliente || 'Cliente'}
          dealName={renegotiationPayment?.description || 'Deal'}
          currentBalance={renegotiationPayment?.valor_aberto || 0}
        />

        <RegisterContactModal
          open={Boolean(registerContactPayment)}
          onClose={() => {
            setRegisterContactPayment(null);
            setRegisterContactPrefill(undefined);
          }}
          paymentId={registerContactPayment?.payment_id || ''}
          contactId={registerContactPayment?.contact_id || ''}
          clientName={registerContactPayment?.cliente || 'Cliente'}
          prefill={registerContactPrefill}
          onSaved={() => {
            refetch();
          }}
        />

        <Dialog open={Boolean(callFallback)} onOpenChange={(open) => !open && setCallFallback(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Fallback de Ligação</DialogTitle>
              <DialogDescription>
                {callFallback?.reason}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="rounded-md border p-3">
                <p className="text-sm text-muted-foreground">Número para ligação</p>
                <p className="font-mono text-base">{callFallback?.phoneE164 || '-'}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Após a ligação, registre o resultado para manter promessa de pagamento e histórico de cobrança atualizados.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCallFallback(null)}>
                Fechar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (!callFallback) return;
                  window.open(`tel:${callFallback.phoneE164}`, '_self');
                }}
              >
                <Phone className="h-4 w-4 mr-2" />
                Ligar no Telefone
              </Button>
              <Button
                onClick={() => {
                  if (!callFallback) return;
                  openRegisterContactModal(callFallback.payment, { contact_type: 'call', reason: 'collection' });
                  setCallFallback(null);
                }}
              >
                Registrar Contato
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Cancel Modal */}
        <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancelar Pagamento</DialogTitle>
              <DialogDescription>
                {selectedPayment?.cliente} - <MoneyDisplay 
                  value={selectedPayment?.valor_aberto || 0} 
                  currency={(selectedPayment?.currency as 'USD' | 'BRL') || 'USD'} 
                />
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Motivo do Cancelamento *</Label>
                <Select value={cancellationReason} onValueChange={setCancellationReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o motivo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CANCELLATION_REASONS.map((reason) => (
                      <SelectItem key={reason} value={reason}>
                        {reason}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {cancellationReason === 'Outro' && (
                <div className="space-y-2">
                  <Label>Especifique o motivo *</Label>
                  <Textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Descreva o motivo do cancelamento..."
                    rows={3}
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCancelDialogOpen(false)}>
                Voltar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleConfirmCancel}
                disabled={cancelPayment.isPending}
              >
                {cancelPayment.isPending && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                Cancelar Pagamento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Charge Modal */}
        <Dialog open={isChargeDialogOpen} onOpenChange={setIsChargeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cobrar Cliente</DialogTitle>
              <DialogDescription>
                {selectedPayment?.cliente} - {selectedPayment?.description}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Valor em aberto</Label>
                <div className="text-lg font-medium">
                  <MoneyDisplay
                    value={selectedPayment?.valor_aberto || 0}
                    currency={(selectedPayment?.currency as 'USD' | 'BRL') || 'USD'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Canal de cobrança</Label>
                <Select value={chargeMethod} onValueChange={(v) => setChargeMethod(v as ChargeMethod)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="bank_transfer">Transferência</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {chargeMethod === 'stripe' ? (
                <>
                  <div className="space-y-2">
                    <Label>Valor a cobrar</Label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      max={selectedPayment?.valor_aberto || 0}
                      value={chargeAmount}
                      onChange={(e) => setChargeAmount(e.target.value)}
                      placeholder="Ex: 250.00"
                    />
                    <p className="text-xs text-muted-foreground">
                      Máximo: {formatMoney(Number(selectedPayment?.valor_aberto || 0), ((selectedPayment?.currency || 'USD') as 'USD' | 'BRL'))}
                    </p>
                  </div>

                  <div className="rounded-md border bg-muted/40 p-3 text-sm">
                    {loadingCardInfo ? (
                      <span className="text-muted-foreground">Verificando cartão salvo...</span>
                    ) : selectedCardInfo?.hasSavedCard ? (
                      <span>
                        Cartão padrão detectado: <strong>{(selectedCardInfo.brand || 'Card').toUpperCase()}</strong> •••• {selectedCardInfo.last4 || '****'}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Sem cartão padrão detectado. O sistema vai gerar link de pagamento.</span>
                    )}
                  </div>
                </>
              ) : null}

              <div className="space-y-2">
                <Label>Observação da cobrança</Label>
                <Textarea
                  value={chargeNote}
                  onChange={(e) => setChargeNote(e.target.value)}
                  placeholder="Ex: enviado lembrete por WhatsApp, aguardando confirmação..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsChargeDialogOpen(false)}>
                Cancelar
              </Button>
              {selectedPayment ? (
                <Button variant="outline" asChild>
                  <Link to={`/deal-detail?id=${selectedPayment.deal_id}`}>Abrir Deal</Link>
                </Button>
              ) : null}
              <Button
                onClick={handleRegisterCharge}
                disabled={
                  isChargingNow || (
                    chargeMethod === 'stripe' &&
                    (
                      !chargeAmount ||
                      Number(String(chargeAmount).replace(',', '.')) <= 0 ||
                      Number(String(chargeAmount).replace(',', '.')) > Number(selectedPayment?.valor_aberto || 0)
                    )
                  )
                }
              >
                {isChargingNow ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                Cobrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Bulk Action Confirmation */}
        <AlertDialog open={isBulkActionDialogOpen} onOpenChange={setIsBulkActionDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Marcar pagamentos como pagos?</AlertDialogTitle>
              <AlertDialogDescription>
                Você está prestes a marcar {selectedIds.size} pagamentos como pagos.
                Esta ação não pode ser desfeita facilmente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleBulkMarkAsPaid}>
                Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </FinancePageLayout>
  );
}
